// ============================================
// FRANCHISE CONTEXT
// React context for franchise state management
// REFACTORED: Now uses live backend data from AuthContext
// ============================================

import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { 
  SystemRole, 
  hasPermission, 
  canAccessOutlet, 
  filterAccessibleOutlets,
  filterAccessibleFranchisees,
  getDataVisibility,
  isCorporateUser,
  Permission 
} from '@/models/permissions';
import { getEffectiveVatRate, defaultVatRules } from '@/models/pricing';
import { getEffectiveFeeConfig, calculateFranchiseFees } from '@/models/franchise';

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatCurrency = (amount, currencyCode = 'NOK', options = {}) => {
  const { locale = 'en', minimumFractionDigits = 2, maximumFractionDigits = 2 } = options;
  
  const formatter = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits,
    maximumFractionDigits,
  });
  
  return formatter.format(amount);
};

// ============================================
// STATIC FALLBACK DATA (used when backend data not available)
// ============================================

// Default VAT configuration by country
const defaultVatConfig = {
  NO: { standardRate: 0.25, reducedRate: 0.15, takeawayRate: 0.15 },
  SE: { standardRate: 0.25, reducedRate: 0.12, takeawayRate: 0.12 },
  DK: { standardRate: 0.25, reducedRate: 0.25, takeawayRate: 0.25 },
  FI: { standardRate: 0.24, reducedRate: 0.14, takeawayRate: 0.14 },
};

// Default country configuration
const defaultCountries = {
  NO: { code: 'NO', name: 'Norway', currency: 'NOK', locale: 'nb-NO' },
  SE: { code: 'SE', name: 'Sweden', currency: 'SEK', locale: 'sv-SE' },
  DK: { code: 'DK', name: 'Denmark', currency: 'DKK', locale: 'da-DK' },
  FI: { code: 'FI', name: 'Finland', currency: 'EUR', locale: 'fi-FI' },
};

// Default franchise owner (for fee calculation fallback)
const defaultFranchiseOwner = {
  id: 'isf-group',
  name: 'Indian Street Food & Co',
  defaultFees: {
    royaltyRate: 0.05,
    marketingRate: 0.02,
    techFee: 500,
  },
};

// ============================================
// CONTEXT DEFINITION
// ============================================

const FranchiseContext = createContext(null);

export function FranchiseProvider({ children }) {
  const auth = useAuth();
  
  // Map backend user data to franchise context user format
  const currentUser = useMemo(() => {
    if (!auth.user) {
      return {
        id: 'anonymous',
        email: '',
        name: 'Guest',
        role: SystemRole.OUTLET_STAFF,
        scope: { type: 'outlet', franchiseGroupId: null, franchiseeId: null, outletIds: [] },
        additionalPermissions: [],
        deniedPermissions: [],
        isActive: false,
      };
    }
    
    // Map backend role to SystemRole
    const roleMapping = {
      'super_admin': SystemRole.CORPORATE_ADMIN,
      'admin': SystemRole.CORPORATE_ADMIN,
      'corporate_admin': SystemRole.CORPORATE_ADMIN,
      'corporate_finance': SystemRole.CORPORATE_FINANCE,
      'franchise_owner': SystemRole.FRANCHISE_OWNER,
      'franchise_admin': SystemRole.FRANCHISE_ADMIN,
      'manager': SystemRole.OUTLET_MANAGER,
      'outlet_manager': SystemRole.OUTLET_MANAGER,
      'staff': SystemRole.OUTLET_STAFF,
      'outlet_staff': SystemRole.OUTLET_STAFF,
    };
    
    const mappedRole = roleMapping[auth.user.role?.toLowerCase()] || SystemRole.OUTLET_STAFF;
    
    // Determine scope type based on role
    let scopeType = 'outlet';
    if ([SystemRole.CORPORATE_ADMIN, SystemRole.CORPORATE_FINANCE, SystemRole.CORPORATE_OPS].includes(mappedRole)) {
      scopeType = 'global';
    } else if ([SystemRole.FRANCHISE_OWNER, SystemRole.FRANCHISE_ADMIN].includes(mappedRole)) {
      scopeType = 'franchise';
    }
    
    return {
      id: auth.user.id,
      email: auth.user.email,
      name: auth.user.full_name || auth.user.email,
      role: mappedRole,
      scope: {
        type: scopeType,
        franchiseGroupId: auth.user.tenant_id,
        franchiseeId: auth.user.franchisee_id || null,
        outletIds: auth.user.outlet_ids || (auth.currentOutlet ? [auth.currentOutlet.id] : []),
      },
      additionalPermissions: auth.permissions || [],
      deniedPermissions: [],
      isActive: auth.user.is_active !== false,
    };
  }, [auth.user, auth.currentOutlet, auth.permissions]);
  
  // Use live outlets from AuthContext
  const outlets = useMemo(() => {
    return auth.outlets || [];
  }, [auth.outlets]);
  
  // Selected outlet state - initialized from AuthContext
  const selectedOutletId = auth.currentOutlet?.id || null;
  
  // Handle outlet selection change
  const handleSetSelectedOutletId = useCallback((outletId) => {
    // Update AuthContext which will trigger re-render
    const outlet = outlets.find(o => o.id === outletId);
    if (outlet && auth.switchOutlet) {
      auth.switchOutlet(outlet);
    }
  }, [outlets, auth]);
  
  // Build franchisees list from outlets (group by franchisee_id)
  const franchisees = useMemo(() => {
    const franchiseeMap = new Map();
    
    outlets.forEach(outlet => {
      const franchiseeId = outlet.franchisee_id || 'corporate';
      if (!franchiseeMap.has(franchiseeId)) {
        franchiseeMap.set(franchiseeId, {
          id: franchiseeId,
          name: outlet.franchisee_name || 'Corporate',
          type: franchiseeId === 'corporate' ? 'corporate' : 'franchise',
          outletIds: [],
        });
      }
      franchiseeMap.get(franchiseeId).outletIds.push(outlet.id);
    });
    
    return Array.from(franchiseeMap.values());
  }, [outlets]);
  
  // ============================================
  // DERIVED STATE
  // ============================================
  
  // Get accessible outlets for current user
  const accessibleOutlets = useMemo(() => {
    return filterAccessibleOutlets(currentUser, outlets);
  }, [currentUser, outlets]);
  
  // Get accessible franchisees for current user
  const accessibleFranchisees = useMemo(() => {
    return filterAccessibleFranchisees(currentUser, franchisees);
  }, [currentUser, franchisees]);
  
  // Get current outlet
  const currentOutlet = useMemo(() => {
    return outlets.find(o => o.id === selectedOutletId) || auth.currentOutlet;
  }, [outlets, selectedOutletId, auth.currentOutlet]);
  
  // Get current franchisee (of selected outlet)
  const currentFranchisee = useMemo(() => {
    if (!currentOutlet) return null;
    return franchisees.find(f => f.id === (currentOutlet.franchisee_id || 'corporate'));
  }, [currentOutlet, franchisees]);
  
  // Get country config for current outlet
  const currentCountry = useMemo(() => {
    if (!currentOutlet) return defaultCountries.NO;
    const countryCode = currentOutlet.country || currentOutlet.address?.country || 'NO';
    return defaultCountries[countryCode] || defaultCountries.NO;
  }, [currentOutlet]);
  
  // Get VAT config for current outlet's country
  const currentVatConfig = useMemo(() => {
    if (!currentOutlet) return defaultVatConfig.NO;
    const countryCode = currentOutlet.country || currentOutlet.address?.country || 'NO';
    return defaultVatConfig[countryCode] || defaultVatConfig.NO;
  }, [currentOutlet]);
  
  // Data visibility level
  const dataVisibility = useMemo(() => {
    return getDataVisibility(currentUser);
  }, [currentUser]);
  
  // Is corporate user (cross-franchise visibility)
  const isCorporate = useMemo(() => {
    return isCorporateUser(currentUser);
  }, [currentUser]);
  
  // ============================================
  // PERMISSION CHECKS
  // ============================================
  
  const checkPermission = useCallback((permission) => {
    return hasPermission(currentUser, permission);
  }, [currentUser]);
  
  const checkOutletAccess = useCallback((outletId) => {
    const outlet = outlets.find(o => o.id === outletId);
    return canAccessOutlet(currentUser, outletId, outlet);
  }, [currentUser, outlets]);
  
  // ============================================
  // VAT & PRICING FUNCTIONS
  // ============================================
  
  const getVatRate = useCallback((orderType, itemCategory) => {
    if (!currentOutlet) return { rate: 0.25, source: 'fallback' };
    
    const countryCode = currentOutlet.country || currentOutlet.address?.country || 'NO';
    
    return getEffectiveVatRate({
      countryCode,
      outletId: currentOutlet.id,
      franchiseeId: currentOutlet.franchisee_id,
      orderType,
      itemCategory,
    }, defaultVatRules);
  }, [currentOutlet]);
  
  const formatPrice = useCallback((amount, options = {}) => {
    const currency = currentOutlet?.currency || currentCountry.currency || 'NOK';
    return formatCurrency(amount, currency, options);
  }, [currentOutlet, currentCountry]);
  
  // ============================================
  // FRANCHISE FEE FUNCTIONS
  // ============================================
  
  const getFeeConfig = useCallback((franchiseeId) => {
    const franchisee = franchisees.find(f => f.id === franchiseeId);
    if (!franchisee) return null;
    return getEffectiveFeeConfig(defaultFranchiseOwner, franchisee);
  }, [franchisees]);
  
  const calculateFees = useCallback((franchiseeId, salesData) => {
    const feeConfig = getFeeConfig(franchiseeId);
    if (!feeConfig) return null;
    return calculateFranchiseFees(salesData, feeConfig);
  }, [getFeeConfig]);
  
  // ============================================
  // USER SWITCHING (for demo/testing - uses AuthContext)
  // ============================================
  
  const switchUser = useCallback((userKey) => {
    // In production, user switching is handled by AuthContext login/logout
    // This is kept for backward compatibility with demo mode
    console.warn('switchUser is deprecated. Use AuthContext login/logout instead.');
  }, []);
  
  // Sample users for testing (kept for backward compatibility)
  const sampleUsers = useMemo(() => ({
    corporateAdmin: {
      id: 'demo-corp-admin',
      email: 'admin@isfco.no',
      name: 'Corporate Admin',
      role: SystemRole.CORPORATE_ADMIN,
      scope: { type: 'global', franchiseGroupId: auth.tenantId, franchiseeId: null, outletIds: [] },
    },
    outletManager: {
      id: 'demo-outlet-manager',
      email: 'manager@isfco.no',
      name: 'Outlet Manager',
      role: SystemRole.OUTLET_MANAGER,
      scope: { type: 'outlet', franchiseGroupId: auth.tenantId, franchiseeId: 'corporate', outletIds: auth.currentOutlet ? [auth.currentOutlet.id] : [] },
    },
  }), [auth.tenantId, auth.currentOutlet]);
  
  // ============================================
  // CONTEXT VALUE
  // ============================================
  
  const value = useMemo(() => ({
    // User (from backend via AuthContext)
    currentUser,
    setCurrentUser: () => console.warn('setCurrentUser is deprecated. Use AuthContext.'),
    switchUser,
    sampleUsers,
    
    // Outlet selection
    selectedOutletId,
    setSelectedOutletId: handleSetSelectedOutletId,
    currentOutlet,
    currentFranchisee,
    currentCountry,
    currentVatConfig,
    
    // Access control
    accessibleOutlets,
    accessibleFranchisees,
    dataVisibility,
    isCorporate,
    checkPermission,
    checkOutletAccess,
    
    // Franchise data (live from backend)
    franchiseOwner: defaultFranchiseOwner,
    franchisees,
    outlets,
    
    // VAT & Pricing
    getVatRate,
    formatPrice,
    vatConfig: defaultVatConfig,
    countries: defaultCountries,
    
    // Fees
    getFeeConfig,
    calculateFees,
    
    // Permissions enum for convenience
    Permission,
    
    // Auth state passthrough
    isAuthenticated: auth.isAuthenticated,
    isLoading: auth.loading,
  }), [
    currentUser,
    switchUser,
    sampleUsers,
    selectedOutletId,
    handleSetSelectedOutletId,
    currentOutlet,
    currentFranchisee,
    currentCountry,
    currentVatConfig,
    accessibleOutlets,
    accessibleFranchisees,
    dataVisibility,
    isCorporate,
    checkPermission,
    checkOutletAccess,
    franchisees,
    outlets,
    getVatRate,
    formatPrice,
    getFeeConfig,
    calculateFees,
    auth.isAuthenticated,
    auth.loading,
  ]);
  
  return (
    <FranchiseContext.Provider value={value}>
      {children}
    </FranchiseContext.Provider>
  );
}

// ============================================
// HOOK
// ============================================

export function useFranchise() {
  const context = useContext(FranchiseContext);
  if (!context) {
    throw new Error('useFranchise must be used within a FranchiseProvider');
  }
  return context;
}

// ============================================
// HIGHER-ORDER COMPONENT FOR PERMISSION CHECKS
// ============================================

export function withPermission(WrappedComponent, requiredPermission) {
  return function PermissionGuard(props) {
    const { checkPermission } = useFranchise();
    
    if (!checkPermission(requiredPermission)) {
      return (
        <div className="p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-700">Access Denied</h2>
          <p className="text-gray-500 mt-2">You do not have permission to view this content.</p>
        </div>
      );
    }
    
    return <WrappedComponent {...props} />;
  };
}

// ============================================
// PERMISSION GUARD COMPONENT
// ============================================

export function PermissionGate({ permission, children, fallback = null }) {
  const { checkPermission } = useFranchise();
  
  if (!checkPermission(permission)) {
    return fallback;
  }
  
  return children;
}

// ============================================
// OUTLET GUARD COMPONENT
// ============================================

export function OutletGate({ outletId, children, fallback = null }) {
  const { checkOutletAccess } = useFranchise();
  
  if (!checkOutletAccess(outletId)) {
    return fallback;
  }
  
  return children;
}
